import {
  Card,
  Typography,
} from "antd";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import consentExpiry from "../../../../Assets/consentExpiry.png";
import consentType from "../../../../Assets/consentTypes.png";
import customerId from "../../../../Assets/customerIs.png";
import fiDataLife from "../../../../Assets/fiDataLife.png";
import fidatapull from "../../../../Assets/fiDataPullFrequency.png";
import fiDataRange from "../../../../Assets/fiDataRange.png";
import fiType from "../../../../Assets/fiType.png";
import purpose from "../../../../Assets/purpose.png";

import { ReactComponent as Timer } from "../../../../Assets/timer.svg";

import { translation } from "../../../../i18n/translationConstant";
import { ConsentDetail } from "../../../../Models/Response/Consent/GetAllConsent";
import GetConsentByHandleResponse from "../../../../Models/Response/Consent/GetConsentByHandle";
import { GetConsentByIdResponse } from "../../../../Models/Response/Consent/GetConsentById";

import TimeFromNow from "../../../Common/Components/TimeFromNow";
import { useFingerprintValidation } from "../../../Authentication/FingerprintValidationContext";

import "./index.css";
import { ArrowRightOutlined, BankOutlined, CustomerServiceOutlined, RollbackOutlined, SafetyCertificateOutlined, TeamOutlined } from "@ant-design/icons";

interface IConsentDetailsProps extends React.PropsWithChildren {
  consentByUniqueIdDetails:
    | GetConsentByIdResponse
    | GetConsentByHandleResponse;
  state?: ConsentDetail | any;
}

const columns = [
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
  },
  {
    title: "Operator",
    dataIndex: "operator",
    key: "operator",
  },
  {
    title: "Value",
    dataIndex: "value",
    key: "value",
  },
];

interface DetailItemProps {
  icon: string;
  title: React.ReactNode;
  value: React.ReactNode;
}

const DetailItem: React.FC<DetailItemProps> = ({
  icon,
  title,
  value,
}) => {
  return (
    <div className="consentDetailItem">
      <img
        src={icon}
        alt=""
        className="consentDetailIcon"
      />

      <div className="consentDetailItemContent">
        <Typography.Text className="consentDetailItemTitle">
          {title}
        </Typography.Text>

        <Typography.Text className="consentDetailItemValue">
          {value}
        </Typography.Text>
      </div>
    </div>
  );
};

const ConsentDetails: React.FC<IConsentDetailsProps> = ({
  consentByUniqueIdDetails,
  children,
  state,
}) => {
  const [t] = useTranslation();

  const { checkFingerprint } =
    useFingerprintValidation();

  useEffect(() => {
    checkFingerprint();
  }, [checkFingerprint]);

  const consent = consentByUniqueIdDetails;

  const getConsentType = () => {
    if (!consent.consentTypes) {
      return "";
    }

    return consent.consentTypes.join(", ");
  };

  const getFiTypes = () => {
    if (!consent.fiTypes) {
      return "";
    }

    return consent.fiTypes.join(", ");
  };

  const getConsentExpiry = () => {
    if (!consent.consentExpiry) {
      return "-";
    }

    return new Date(consent.consentExpiry)
      .toDateString()
      .slice(4);
  };

  const getConsentStart = () => {
    if (!consent.consentStart) {
      return "-";
    }

    return new Date(consent.consentStart)
      .toDateString()
      .slice(4);
  };

  const getFiDataRange = () => {
    if (!consent.FIDataRange) {
      return "-";
    }

    return (
      <>
        {t(translation.From)}{" "}
        {new Date(consent.FIDataRange.from)
          .toDateString()
          .slice(4)}{" "}
        {t(translation.to)}{" "}
        {new Date(consent.FIDataRange.to)
          .toDateString()
          .slice(4)}
      </>
    );
  };

  const getFrequencyValue = () => {
    if (consent.fetchType === "ONETIME") {
      return t(translation.lblInformatioFetch) + " only once";
    }

    if (!consent.Frequency) {
      return "-";
    }

    return (
      <>
        {t(translation.lblInformatioFetch)}{" "}
        {consent.Frequency.value}{" "}
        {t(translation.lblTimes)}{" "}
        {consent.Frequency.unit}/s
      </>
    );
  };

  const getFIHeader = () => {
    const fiName =
      state?.fiuName ||
      "Financial Information User";

    const updatedOn = state?.updatedOn;

    return (
      <div className="consentInstitutionCard">

        <div className="consentInstitutionLeft">

          <div className="consentInstitutionLogo">
            {state?.logoUrl ? (
              <img
                src={state.logoUrl}
                alt=""
              />
            ) : (
              <span>FI</span>
            )}
          </div>

          <div className="consentInstitutionInfo">

            <Typography.Text className="consentInstitutionName">
              {fiName}
            </Typography.Text>

            <Typography.Text className="consentInstitutionSubText">
              {t(
                translation.lblWantsToAccessAccount
              )}
            </Typography.Text>

          </div>

        </div>

        <div className="consentInstitutionStatus">

          <div className="consentPendingInfo">
            <Timer className="consentPendingIcon" />

            <div>
              {
              (state?.consentApprovalStatusType === "PENDING")?
                <Typography.Text className="consentPendingTitle">
                  {t(translation.lblPendingYourAction)}
                </Typography.Text>
                : 
                ""
              } 

              {updatedOn && (
                <TimeFromNow
                  className="consentPendingTime"
                  date={updatedOn}
                />
              )}
            </div>
          </div>

          <span className="consentPendingBadge">
            {state?.consentApprovalStatusType}
          </span>

        </div>

      </div>
    );
  };

  return (
    <div className="commonConsentDetails">

      {/* FI Header */}
      {getFIHeader()}

      {/* Permission Card */}
      <Card
        bordered={false}
        className="consentPermissionCard"
      >

        <div className="consentSectionHeader">

          <div className="consentSectionHeaderIcon">
            <img
              src={fidatapull}
              alt=""
            />
          </div>

          <div>
            <Typography.Text className="consentSectionTitle">
              {t(
                translation.lblAccountPermissionAndInfoAccess
              )}
            </Typography.Text>

            <Typography.Text className="consentSectionSubtitle">
              {t(translation.lblReviewInformationAccess)}
            </Typography.Text>
          </div>

        </div>

        <div className="consentPermissionGrid">

          {/* LEFT COLUMN */}
          <div className="consentPermissionColumn">

            <DetailItem
              icon={fidatapull}
              title={
                <>
                  {t(
                    translation.lblFiDataPullFrequency
                  )}{" "}
                  ({consent.fetchType})
                </>
              }
              value={getFrequencyValue()}
            />

            <DetailItem
              icon={purpose}
              title={t(translation.PURPOSE)}
              value={
                <>
                  {consent.Purpose?.code} -{" "}
                  {consent.Purpose?.text}
                </>
              }
            />

            {/* <DetailItem
              icon={consentMode}
              title={t(translation.lblConsentMode)}
              value={consent.consentMode}
            /> */}

            {/* <div className="consentDetailItem">

              <img
                src={dataFliters}
                alt=""
                className="consentDetailIcon"
              />

              <div className="consentDetailItemContent consentFilterContent">

                <Collapse
                  ghost
                  className="consentFilterCollapse"
                >
                  <Collapse.Panel
                    key="filters"
                    header={t(
                      translation.lblDataFilters
                    )}
                  >
                    <Table
                      columns={columns}
                      dataSource={
                        consent.DataFilter || []
                      }
                      pagination={false}
                      size="small"
                    />
                  </Collapse.Panel>
                </Collapse>

              </div>

            </div> */}

            <DetailItem
              icon={fiDataRange}
              title={t(translation.lblFiDataRange)}
              value={getFiDataRange()}
            />

            <DetailItem
              icon={fiDataLife}
              title={t(translation.lblFiDataLife)}
              value={
                <>
                  {consent.DataLife?.value}{" "}
                  {consent.DataLife?.unit}
                </>
              }
            />

          </div>

          {/* RIGHT COLUMN */}
          <div className="consentPermissionColumn">

            <DetailItem
              icon={consentType}
              title={t(translation.lblConsentType)}
              value={getConsentType()}
            />

            <DetailItem
              icon={consentExpiry}
              title={t(translation.lblConsentExpiry)}
              value={getConsentExpiry()}
            />

            {/* <DetailItem
              icon={consumerId}
              title={t(translation.lblConsumerId)}
              value={
                consent.DataConsumer?.id || "-"
              }
            /> */}

            <DetailItem
              icon={customerId}
              title={t(translation.lblConsentStart)}
              value={getConsentStart()}
            />

            <DetailItem
              icon={fiType}
              title={t(translation.FIType)}
              value={getFiTypes()}
            />

          </div>

        </div>
        
        {/* Existing child content remains untouched */}
        {children}


        <div className="complaintSection">

          <div className="complaintIntro">
            <div className="complaintIntroIcon">
              <CustomerServiceOutlined />
            </div>

            <div className="complaintIntroContent">
              <Typography.Text className="complaintSectionTitle">
                {t(translation.lblNeedHelp)}
              </Typography.Text>

              <Typography.Text className="complaintSectionSubtitle">
                {t(translation.lblContactOrganisation)}
              </Typography.Text>
            </div>
          </div>


          <div className="complaintOptions">

            {/* ROllback */}
            <div className="complaintOption">

              <div className="complaintOptionIcon">
                <RollbackOutlined />
              </div>

              <div className="complaintOptionContent">
                <Typography.Text className="complaintOptionTitle">
                  {t(translation.lblConsentWithdrawal)}
                </Typography.Text>

                <Typography.Text 
                  className="complaintOptionLink" 
                  onClick={() => window.open("https://www.ink-aa.com/consent-withdrawal.html", "_blank")}
                >
                  {t(translation.lblViewWithdrawalProcess)}
                  <ArrowRightOutlined />
                </Typography.Text>
              </div>

            </div>

            {/* FIU */}
            <div className="complaintOption">

              <div className="complaintOptionIcon">
                <BankOutlined />
              </div>

              <div className="complaintOptionContent">
                <Typography.Text className="complaintOptionTitle">
                  {t(translation.lblComplainToFIU)}
                </Typography.Text>

                <Typography.Text 
                  className="complaintOptionLink"
                  onClick={() => window.open("https://www.ink-aa.com/grievances-details.html", "_blank")}
                >
                  {t(translation.lblViewComplaintProcess)}
                  <ArrowRightOutlined />
                </Typography.Text>
              </div>

            </div>


            {/* AA */}
            <div className="complaintOption">

              <div className="complaintOptionIcon">
                <TeamOutlined />
              </div>

              <div className="complaintOptionContent">
                <Typography.Text className="complaintOptionTitle">
                  {t(translation.lblComplainToAA)}
                </Typography.Text>

                <Typography.Text 
                  className="complaintOptionLink"
                  onClick={() => window.open("https://www.ink-aa.com/grievances-details.html", "_blank")}
                >
                  {t(translation.lblViewComplaintProcess)}
                  <ArrowRightOutlined />
                </Typography.Text>
              </div>

            </div>


            {/* DATA PROTECTION BOARD */}


            <div className="complaintOption">

              <div className="complaintOptionIcon">
                <SafetyCertificateOutlined />
              </div>

              <div className="complaintOptionContent">
                <Typography.Text className="complaintOptionTitle">
                  {t(translation.lblComplainToDPB)}
                </Typography.Text>

                <Typography.Text className="complaintOptionLink">
                   {t(translation.lblMoreInformationComingSoon)}
                  <ArrowRightOutlined />
                </Typography.Text>
              </div>

            </div>

          </div>

        </div>

      </Card>

    </div>
  );
};

export default ConsentDetails;